import { useState, useEffect, useRef } from "react";
import { Box } from "@mui/material";
import ArrowBackIosIcon from "@mui/icons-material/ArrowBackIos";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import Hls from 'hls.js';
import MissingContent from "../../../../components/shared/errorMessages/MissingContent";
import { useNavigate } from 'react-router-dom';
import {
  MediaOption,
  CarouselMediaItem,
  CMSViewMode
} from '../../types/CMSTypes';
import { getCategoryDetails, buildCategoryUrl } from '../../api/cmsApi';

interface CMSConsumerCarouselBannerProps {
  bannerMediaOption: MediaOption;
  bannerMediaCarousel: CarouselMediaItem[];
  bannerMediaVideo: string | null;
  bannerMediaVideoLink: string;
  bannerLinkCategoryId: string | null;
  bannerWidth: number;
  bannerHeight: number;
  viewMode?: CMSViewMode;
  aspectRatio?: string;
}

const CMSConsumerCarouselBanner: React.FC<CMSConsumerCarouselBannerProps> = ({
                                                                               bannerMediaOption = 'carousel',
                                                                               bannerMediaCarousel = [],
                                                                               bannerMediaVideo = null,
                                                                               bannerMediaVideoLink = '',
                                                                               bannerLinkCategoryId = null,
                                                                               bannerWidth = 2880,
                                                                               bannerHeight = 1050,
                                                                               viewMode = 'live',
                                                                               aspectRatio = "36.46%"
                                                                             }) => {
  const navigate = useNavigate();

  const calculateAspectRatio = (): string => {
    if (bannerWidth && bannerHeight && bannerWidth > 0 && bannerHeight > 0) {
      const calculatedRatio = (bannerHeight / bannerWidth) * 100;
      return `${calculatedRatio}%`;
    }
    return aspectRatio;
  };

  const dynamicAspectRatio = calculateAspectRatio();

  // TEMP WORKAROUND: some pages still store the raw `.mp4` filename because the
  // backend transcode write-back didn't flip the value to its HLS version, so
  // the heavy MP4 gets served (laggy) even though the `_master.m3u8` exists in
  // S3. Point the player at the HLS variant up front — the transcoder names it
  // `{stem}_master.m3u8` next to the original. If that variant doesn't exist,
  // the HLS error handler below already falls back to the original `.mp4`
  // (replace('_master.m3u8', '.mp4')), so videos without a transcode are
  // unaffected. Remove once stored values are reliably `.m3u8`.
  const toHlsVariant = (url: string): string => {
    if (!url || url.includes('.m3u8')) return url;
    return url.replace(/\.mp4(\?.*)?$/i, '_master.m3u8$1');
  };

  const createCategoryLink = async (categoryId: string): Promise<string> => {
    try {
      const categoryResponse = await getCategoryDetails(categoryId, viewMode);
      if (categoryResponse.success && categoryResponse.cms_category) {
        return buildCategoryUrl(categoryResponse.cms_category);
      }
    } catch (error) {
      console.error('Error building category link:', error);
    }
    return `/category/${categoryId}`;
  };

  const handleItemClick = async () => {
    if (bannerLinkCategoryId) {
      const categoryLink = await createCategoryLink(bannerLinkCategoryId);
      navigate(categoryLink);
    }
  };

  const buildItemsArray = () => {
    const items: Array<{ url: string; isVideo: boolean; hasLink: boolean }> = [];

    switch (bannerMediaOption) {
      case 'video':
        if (bannerMediaVideo && bannerMediaVideoLink) {
          items.push({
            url: toHlsVariant(bannerMediaVideoLink),
            isVideo: true,
            hasLink: !!bannerLinkCategoryId
          });
        }
        break;

      case 'carousel':
      case 'single':
      default:
        bannerMediaCarousel.forEach(item => {
          if (item.media_url) {
            items.push({
              url: item.media_url,
              isVideo: false,
              hasLink: !!bannerLinkCategoryId
            });
          }
        });
        break;
    }

    return items;
  };

  const [items, setItems] = useState<Array<{ url: string; isVideo: boolean; hasLink: boolean }>>([]);
  const [currentIndex, setCurrentIndex] = useState<number>(0);
  const [containerReady, setContainerReady] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const videoRef = useRef<HTMLVideoElement>(null);
  const hlsRef = useRef<Hls | null>(null);

  useEffect(() => {
    const newItems = buildItemsArray();
    setItems(newItems);
    setCurrentIndex(0);
  }, [
    bannerMediaOption,
    bannerMediaCarousel,
    bannerMediaVideo,
    bannerMediaVideoLink,
    bannerLinkCategoryId
  ]);

  useEffect(() => {
    if (items.length > 0) {
      setContainerReady(true);
    }
  }, [items]);

  const next = () => {
    if (items.length === 0) return;
    setCurrentIndex((prevIndex) => (prevIndex + 1) % items.length);
  };

  const previous = () => {
    if (items.length === 0) return;
    setCurrentIndex((prevIndex) => (prevIndex - 1 + items.length) % items.length);
  };

  useEffect(() => {
    if (items.length === 0) return;

    const itemsToPreload = [
      items[currentIndex],
      items[(currentIndex + 1) % items.length]
    ].filter(Boolean);

    itemsToPreload.forEach((item) => {
      if (!item?.url || item.isVideo) return;
      const img = new Image();
      img.src = item.url;
    });
  }, [items, currentIndex]);

  useEffect(() => {
    if (items.length <= 1) return;

    if (bannerMediaOption === 'video' && items.length === 1 && items[0]?.isVideo) {
      return;
    }

    const interval = setInterval(() => {
      setCurrentIndex(prevIndex => (prevIndex + 1) % items.length);
    }, 3000);

    return () => clearInterval(interval);
  }, [items.length, bannerMediaOption]);

  useEffect(() => {
    const currentItem = items[currentIndex];

    if (!currentItem?.isVideo || !videoRef.current) {
      if (containerReady && currentItem?.isVideo && !videoRef.current) {
        const timeoutId = setTimeout(() => {
          console.log('Retrying video mount');
        }, 100);
        return () => clearTimeout(timeoutId);
      }
      return;
    }

    const videoElement = videoRef.current;
    const isHLS = currentItem.url.includes('.m3u8');

    if (isHLS) {
      if (hlsRef.current) {
        hlsRef.current.destroy();
        hlsRef.current = null;
      }

      if (Hls.isSupported()) {
        const hls = new Hls({
          maxLoadingDelay: 4,
          lowLatencyMode: false,       // disabled — not a low-latency stream
          startLevel: -1,              // auto-select start level to avoid buffer underrun
          capLevelToPlayerSize: true,  // cap quality to container size, reduces initial load
          autoStartLoad: true,
          enableWorker: true,
          maxBufferLength: 30,         // ensure enough buffer before playback starts
          maxMaxBufferLength: 60,
        });

        hls.loadSource(currentItem.url);
        hls.attachMedia(videoElement);

        hls.on(Hls.Events.MANIFEST_PARSED, () => {
          // Cap a decorative background banner to <=720p: the 1080p rendition's
          // heavier (cold-cached) segments are the main first-load delay, and a
          // banner doesn't need full HD. Cap ABR to the highest level at or
          // below 720p.
          let capIdx = -1;
          let capHeight = -1;
          hls.levels.forEach((lvl, i) => {
            if (lvl.height && lvl.height <= 720 && lvl.height > capHeight) {
              capHeight = lvl.height;
              capIdx = i;
            }
          });
          if (capIdx >= 0) hls.autoLevelCapping = capIdx;
          videoElement.play().catch((error) => {
            console.warn('Autoplay failed:', error);
          });
        });

        videoElement.addEventListener('loadedmetadata', () => {
          const container = containerRef.current;
          if (container) {
            console.log('📦 Container dimensions:', {
              width: container.offsetWidth,
              height: container.offsetHeight,
              containerAspectRatio: (container.offsetWidth / container.offsetHeight).toFixed(2)
            });
          }
        });

        hls.on(Hls.Events.ERROR, (event, data) => {
          // Suppress known non-fatal buffering noise
          const suppressedDetails = [
            Hls.ErrorDetails.BUFFER_STALLED_ERROR,
            Hls.ErrorDetails.BUFFER_SEEK_OVER_HOLE,
          ];

          if (!data.fatal && suppressedDetails.includes(data.details)) return;

          if (data.fatal) {
            switch (data.type) {
              case Hls.ErrorTypes.NETWORK_ERROR:
                if (data.response?.code === 404) {
                  console.warn('HLS not found, trying MP4 fallback');
                  const mp4Url = currentItem.url.replace('_master.m3u8', '.mp4');
                  videoElement.src = mp4Url;
                  videoElement.play().catch(() => {
                    console.error('MP4 fallback failed');
                  });
                } else {
                  hls.startLoad();
                }
                break;
              case Hls.ErrorTypes.MEDIA_ERROR:
                hls.recoverMediaError();
                break;
              default:
                hls.destroy();
                break;
            }
          } else {
            console.error('❌ HLS error:', data);
          }
        });

        hlsRef.current = hls;

      } else if (videoElement.canPlayType('application/vnd.apple.mpegurl')) {
        console.log('Using native HLS support');
        videoElement.src = currentItem.url;
        videoElement.play().catch((error) => {
          console.warn('Native HLS autoplay failed:', error);
        });
      } else {
        console.error('❌ HLS not supported');
      }
    } else {
      if (hlsRef.current) {
        hlsRef.current.destroy();
        hlsRef.current = null;
      }

      videoElement.src = currentItem.url;
      videoElement.load();
      videoElement.play().catch((error) => {
        console.error('MP4 playback failed:', error);
      });
    }

    return () => {
      if (hlsRef.current) {
        hlsRef.current.destroy();
        hlsRef.current = null;
      }
    };
  }, [currentIndex, items, containerReady]);

  const handleVideoEnd = () => {
    if (items.length > 1 && bannerMediaOption !== 'video') {
      next();
    }
  };

  const showNavigation = items.length > 1 &&
    !(bannerMediaOption === 'video' && items[0]?.isVideo);

  if (items.length === 0) {
    return <MissingContent sectionName="Banner Carousel" />;
  }

  return (
    <Box sx={{
      marginTop: 0,
      paddingTop: 0,
      borderTop: 'none',
      display: 'block'
    }}>
      <Box
        ref={containerRef}
        sx={{
          width: "100%",
          position: "relative",
          overflow: "hidden",
          paddingTop: dynamicAspectRatio,
          marginTop: 0,
          borderTop: 'none',
          display: 'block'
        }}
      >
        {containerReady && items.map((item, index) => (
          item && (
            <Box
              key={`${item.url}-${index}`}
              className={`slide ${index === currentIndex ? "active" : ""}`}
              sx={{
                width: "100%",
                height: "100%",
                position: "absolute",
                top: 0,
                left: 0,
                cursor: item.hasLink ? "pointer" : "default",
                transition: 'opacity 0.5s ease-in-out',
                opacity: index === currentIndex ? 1 : 0,
                '& img, & video': {
                  display: 'block',
                  maxWidth: '100%',
                  height: 'auto',
                }
              }}
              onClick={handleItemClick}
            >
              {item.isVideo ? (
                <video
                  key={item.url}
                  ref={index === currentIndex ? videoRef : null}
                  style={{
                    width: "100%",
                    height: "100%",
                    position: "absolute",
                    top: 0,
                    left: 0,
                    objectFit: "cover",
                    display: 'block',
                    transform: 'translateZ(0)',
                  }}
                  onEnded={handleVideoEnd}
                  muted
                  autoPlay
                  playsInline
                  loop
                  poster=""
                  preload="metadata"
                  width={bannerWidth}
                  height={bannerHeight}
                >
                  Your browser does not support the video tag.
                </video>
              ) : (
                <img
                  key={item.url}
                  style={{
                    width: "100%",
                    height: "100%",
                    position: "absolute",
                    top: 0,
                    left: 0,
                    objectFit: "cover",
                    display: 'block',
                  }}
                  src={item.url}
                  alt=""
                  loading={index === 0 ? 'eager' : 'lazy'}
                  width={bannerWidth}
                  height={bannerHeight}
                  onError={() => {
                    console.warn(`Failed to load banner image: ${item.url}`);
                  }}
                />
              )}
            </Box>
          )
        ))}

        {showNavigation && containerReady && (
          <>
            <ArrowBackIosIcon
              sx={{
                position: "absolute",
                bottom: "50%",
                left: "5%",
                zIndex: 1,
                cursor: "pointer",
                color: "white",
                fontSize: "2rem"
              }}
              onClick={previous}
            />
            <ArrowForwardIosIcon
              sx={{
                position: "absolute",
                bottom: "50%",
                right: "5%",
                zIndex: 1,
                cursor: "pointer",
                color: "white",
                fontSize: "2rem"
              }}
              onClick={next}
            />
          </>
        )}
      </Box>
    </Box>
  );
};

export default CMSConsumerCarouselBanner;
